import { ReactNode } from 'react';
import {
  Box, Typography, Stack, TextField, Button, InputAdornment, alpha,
  CircularProgress,
} from '@mui/material';
import { Search, Add, FileDownload, Upload } from '@mui/icons-material';

interface Props {
  title: string;
  description?: string;
  groupLabel?: string;
  recordCount?: number;
  totalCount?: number;
  search?: string;
  onSearchChange?: (v: string) => void;
  onAdd: () => void;
  addLabel: string;
  onExport?: () => void;
  onImport?: () => void;
  exportLoading?: boolean;
  catalogTrigger?: ReactNode;
}

export default function MasterTypeHeader({
  title,
  recordCount,
  totalCount,
  search,
  onSearchChange,
  onAdd,
  addLabel,
  onExport,
  onImport,
  exportLoading,
  catalogTrigger,
}: Props) {
  const displayTotal = totalCount ?? recordCount;
  const showSearch = Boolean(onSearchChange);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 1.25,
        px: { xs: 1.75, md: 2.25 },
        py: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        minWidth: 0,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
        {catalogTrigger}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            fontWeight={750}
            noWrap
            sx={{ fontSize: '1.05rem', letterSpacing: '-0.03em', lineHeight: 1.25 }}
          >
            {title}
          </Typography>
          {displayTotal != null && (
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.15 }}>
              {displayTotal} {displayTotal === 1 ? 'record' : 'records'}
            </Typography>
          )}
        </Box>
      </Stack>

      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
        sx={{ minWidth: 0 }}
      >
        {showSearch && (
          <TextField
            size="small"
            placeholder="Search records…"
            value={search ?? ''}
            onChange={(e) => onSearchChange?.(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18 }} color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 220 },
              '& .MuiOutlinedInput-root': {
                height: 36,
                borderRadius: 1.5,
                fontSize: '0.8125rem',
                bgcolor: alpha('#0f172a', 0.02),
              },
            }}
          />
        )}
        {onExport && (
          <Button
            size="small"
            variant="outlined"
            startIcon={exportLoading ? <CircularProgress size={14} /> : <FileDownload sx={{ fontSize: 16 }} />}
            onClick={onExport}
            disabled={exportLoading}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, height: 36, px: 1.4 }}
          >
            Export
          </Button>
        )}
        {onImport && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<Upload sx={{ fontSize: 16 }} />}
            onClick={onImport}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, height: 36, px: 1.4 }}
          >
            Import
          </Button>
        )}
        <Button
          size="small"
          variant="contained"
          startIcon={<Add />}
          onClick={onAdd}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 1.5,
            height: 36,
            px: 1.6,
            boxShadow: 'none',
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {addLabel}
        </Button>
      </Stack>
    </Box>
  );
}
